import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '../util/debounce';

export function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

/**
 * Debounces a callback function. The function will always be called when the
 * delay has passed, even if the component has already been unmounted.
 *
 * @param callback The callback function to debounce.
 * @param delay The delay in milliseconds.
 * @returns The debounced callback function.
 *
 * @see https://www.developerway.com/posts/debouncing-in-react
 */
export const useDebouncedCallback = (callback: () => void, delay: number) => {
    const ref = useRef<() => void>();

    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    const debouncedCallback = useMemo(() => {
        const func = () => {
            ref.current?.();
        };

        return debounce(func, delay);
    }, [delay]);

    return debouncedCallback;
};
